import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { LogOut, Star, Shield, Camera, Edit, Edit2, Check, MapPin, CheckCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ReviewsListModal from '../components/ReviewsListModal';
import PasswordChangeSection from '../components/PasswordChangeSection';
import {
  CircularProgress,
  MultiSelectChips,
  SkillsSelector,
  WorkHistoryItem,
  SaveBar
} from '../components/profile';
import { 
  BOHOL_TOWNS,
  WORKER_SKILLS,
  YEARS_OF_EXPERIENCE,
  EDUCATION_LEVELS,
  normalizeEducationLevel,
  getEducationLabel,
  AVAILABILITY_OPTIONS,
  WORK_TYPE_OPTIONS
} from '../lib/constants';
import { BOHOL_MUNICIPALITIES, getBarangaysForMunicipality } from '../data/boholLocations';
import { getRoleDisplayName } from '../lib/roleDisplayNames';
import CertificationsSection from '../components/CertificationsSection';
import { ResumeUploadCard } from '../components/ResumeUploadCard';
import SmsNotificationSettings from '../components/SmsNotificationSettings';
import JobAlertsSetting from '../components/JobAlertsSetting';
import ContinueApplicationCard from '../components/ContinueApplicationCard';

const ProfileNew = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(searchParams.get('edit') === 'true');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [completenessData, setCompletenessData] = useState(null);
  const [loadingCompleteness, setLoadingCompleteness] = useState(false);
  const [availableBarangays, setAvailableBarangays] = useState([]);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Section 1: Basics
    name: '',
    phone: '',
    date_of_birth: '',
    town: '',
    barangay: '',
    tagline: '',
    
    // Section 2: Experience & Skills
    years_of_experience: '',
    skills: [],
    work_history: [],
    education_level: '',
    education_field: '',
    
    // Section 3: Job Preferences
    salary_expectation_type: 'monthly',
    salary_min: 0,
    salary_max: 0,
    availability: [],
    availability_dates: [],
    preferred_locations: [],
    preferred_work_types: [],
    
    // Business Information (for Employers/Owners)
    is_private_household: false,
    business_name: '',
    business_type: '',
    industry: '',
    business_address: '',
    years_in_business: '',
    number_of_employees: '',
    business_description: '',
    website: '',
    facebook_page: ''
  });
  
  // Check if user is a worker
  const isWorker = profile?.role === 'job_seeker' || profile?.role === 'contractor';

  const draftKey = profile?.id ? `bohol_profile_draft_${profile.id}` : null;
  const draftRestoredRef = useRef(false);

  const buildFormDataFromProfile = (p) => ({
    name: p.name || '',
    phone: p.phone || '',
    date_of_birth: p.date_of_birth || '',
    town: p.town || '',
    barangay: p.barangay || '',
    tagline: p.tagline || '',
    years_of_experience: p.years_of_experience || '',
    skills: p.skills || [],
    work_history: p.work_history || [],
    education_level: normalizeEducationLevel(p.education_level),
    education_field: p.education_field || '',
    salary_expectation_type: p.salary_expectation_type || 'monthly',
    salary_min: p.salary_min ?? 0,
    salary_max: p.salary_max ?? 0,
    availability: p.availability || [],
    availability_dates: p.availability_dates || [],
    preferred_locations: p.preferred_locations || [],
    preferred_work_types: p.preferred_work_types || [],
    is_private_household: p.is_private_household || false,
    business_name: p.business_name || '',
    business_type: p.business_type || '',
    industry: p.industry || '',
    business_address: p.business_address || '',
    years_in_business: p.years_in_business || '',
    number_of_employees: p.number_of_employees || '',
    business_description: p.business_description || '',
    website: p.website || '',
    facebook_page: p.social_media_links?.facebook || ''
  });

  const clearDraft = () => {
    if (draftKey) {
      try { sessionStorage.removeItem(draftKey); } catch {}
    }
  };

  // Initialize form data from profile OR restore draft from sessionStorage
  useEffect(() => {
    if (profile) {
      // Only restore draft once per mount — prevent re-firing when profile prop updates (e.g., after auto-save)
      if (!draftRestoredRef.current && draftKey) {
        draftRestoredRef.current = true;
        try {
          const saved = sessionStorage.getItem(draftKey);
          if (saved) {
            const draft = JSON.parse(saved);
            setFormData(draft.formData);
            setEditing(true);
            setHasUnsavedChanges(true);
            if (draft.formData.town) {
              setAvailableBarangays(getBarangaysForMunicipality(draft.formData.town));
            }
            return; // Skip profile initialization — use the draft
          }
        } catch {}
      }

      setFormData(buildFormDataFromProfile(profile));

      // Load available barangays for the user's town
      if (profile.town) {
        setAvailableBarangays(getBarangaysForMunicipality(profile.town));
      }
    }
  }, [profile]);
  
  // Update available barangays when town changes
  useEffect(() => {
    if (formData.town) {
      setAvailableBarangays(getBarangaysForMunicipality(formData.town));
      // Reset barangay if it's not in the new town's list
      const barangays = getBarangaysForMunicipality(formData.town);
      if (formData.barangay && !barangays.includes(formData.barangay)) {
        setFormData(prev => ({ ...prev, barangay: '' }));
      }
    }
  }, [formData.town]);
  
  // Fetch profile completeness
  const fetchCompleteness = async () => {
    if (!isWorker) return;
    
    setLoadingCompleteness(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await api.get('/api/profile/completeness', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      setCompletenessData(response.data);
    } catch (error) {
      console.error('Error fetching completeness:', error);
    } finally {
      setLoadingCompleteness(false);
    }
  };
  
  useEffect(() => {
    fetchCompleteness();
  }, [profile, isWorker]);
  
  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Persist draft to sessionStorage so it survives tab switches
      if (draftKey) {
        try { sessionStorage.setItem(draftKey, JSON.stringify({ formData: updated })); } catch {}
      }
      return updated;
    });
    setHasUnsavedChanges(true);
  };
  
  // Handle avatar upload
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }
    
    setUploadingAvatar(true);
    
    // Show progress toast
    const toastId = toast.loading('Uploading and processing image...');
    
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to backend with automatic square cropping and orientation correction
      const response = await api.post(
        '/api/profile/avatar/upload',
        formData,
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );
      
      if (response.data.success) {
        setProfile(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
        toast.success('Profile picture updated!', { id: toastId });
        fetchCompleteness();
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload avatar', { id: toastId });
    } finally {
      setUploadingAvatar(false);
    }
  };
  
  // Save profile changes
  const handleSave = async () => {
    // Basic required field validation before sending to backend
    if (isWorker && !formData.name?.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.phone?.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (isWorker && !formData.date_of_birth) {
      toast.error('Please enter your date of birth');
      return;
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // CRITICAL FIX: Clean formData based on user role to prevent cross-validation errors
      const cleanedData = { ...formData };
      
      // Strip locked fields for verified seekers — backend will reject them
      if (isWorker && profile?.verified) {
        delete cleanedData.name;
      }

      if (isWorker) {
        // Remove all employer/owner specific fields for job_seekers and contractors
        delete cleanedData.business_name;
        delete cleanedData.business_type;
        delete cleanedData.business_address;
        delete cleanedData.years_in_business;
        delete cleanedData.number_of_employees;
        delete cleanedData.industry;
        delete cleanedData.business_description;
        delete cleanedData.contact_person_name;
        delete cleanedData.business_phone;
        delete cleanedData.website;
        delete cleanedData.facebook_page;
      } else {
        // Remove all worker specific fields for employers and owners
        delete cleanedData.years_of_experience;
        delete cleanedData.skills;
        delete cleanedData.work_history;
        delete cleanedData.education_level;
        delete cleanedData.education_field;
        delete cleanedData.salary_expectation_type;
        delete cleanedData.salary_min;
        delete cleanedData.salary_max;
        delete cleanedData.availability;
        delete cleanedData.availability_dates;
        delete cleanedData.preferred_locations;
        delete cleanedData.preferred_work_types;
        delete cleanedData.date_of_birth;

        // Convert empty strings to null for integer fields (Pydantic validation)
        if (cleanedData.years_in_business === '' || cleanedData.years_in_business === undefined) {
          cleanedData.years_in_business = null;
        }

        // Convert facebook_page to social_media_links JSONB format
        if (cleanedData.facebook_page !== undefined) {
          const existing = profile?.social_media_links || {};
          cleanedData.social_media_links = {
            ...existing,
            facebook: cleanedData.facebook_page?.trim() || null
          };
          delete cleanedData.facebook_page;
        }

        // Strip locked fields for verified employers — backend will reject them
        if (profile?.verified) {
          delete cleanedData.business_name;
          delete cleanedData.town;
        }
      }
      
      console.log('Sending cleaned profile data:', cleanedData);
      console.log('User role:', profile?.role);

      const response = await api.put(
        '/api/profile',
        cleanedData,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      console.log('Profile update response:', response.data);

      if (response.data.success) {
        console.log('Update successful, new profile:', response.data.profile);
        setProfile(response.data.profile);
        setHasUnsavedChanges(false);
        clearDraft();
        setEditing(false);
        if (searchParams.has('edit')) { searchParams.delete('edit'); setSearchParams(searchParams, { replace: true }); }
        toast.success('Profile updated successfully!');
        fetchCompleteness();
      } else {
        console.error('Update returned success=false:', response.data);
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Save error:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to update profile';
      
      if (error.response?.data?.detail) {
        // Handle FastAPI validation errors (array of objects)
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map(err => `${err.loc?.join('.') || 'Field'}: ${err.msg}`)
            .join(', ');
        } 
        // Handle string detail
        else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
        // Handle object detail
        else if (typeof error.response.data.detail === 'object') {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Discard changes
  const handleDiscard = () => {
    setFormData(buildFormDataFromProfile(profile));
    setHasUnsavedChanges(false);
    setLastAutoSaved(null);
    clearDraft();
    setEditing(false);
    if (searchParams.has('edit')) { searchParams.delete('edit'); setSearchParams(searchParams, { replace: true }); }
  };

  // Silent auto-save (no validation, no toast, stays in editing mode)
  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges || !editing || saving) return;

    setAutoSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Clean data the same way as handleSave
      const cleanedData = { ...formData };

      if (isWorker && profile?.verified) {
        delete cleanedData.name;
      }

      if (isWorker) {
        delete cleanedData.business_name;
        delete cleanedData.business_type;
        delete cleanedData.business_address;
        delete cleanedData.years_in_business;
        delete cleanedData.number_of_employees;
        delete cleanedData.industry;
        delete cleanedData.business_description;
        delete cleanedData.contact_person_name;
        delete cleanedData.business_phone;
        delete cleanedData.website;
        delete cleanedData.facebook_page;
      } else {
        delete cleanedData.years_of_experience;
        delete cleanedData.skills;
        delete cleanedData.work_history;
        delete cleanedData.education_level;
        delete cleanedData.education_field;
        delete cleanedData.salary_expectation_type;
        delete cleanedData.salary_min;
        delete cleanedData.salary_max;
        delete cleanedData.availability;
        delete cleanedData.availability_dates;
        delete cleanedData.preferred_locations;
        delete cleanedData.preferred_work_types;
        delete cleanedData.date_of_birth;

        if (cleanedData.years_in_business === '' || cleanedData.years_in_business === undefined) {
          cleanedData.years_in_business = null;
        }

        if (cleanedData.facebook_page !== undefined) {
          const existing = profile?.social_media_links || {};
          cleanedData.social_media_links = {
            ...existing,
            facebook: cleanedData.facebook_page?.trim() || null
          };
          delete cleanedData.facebook_page;
        }

        if (profile?.verified) {
          delete cleanedData.business_name;
          delete cleanedData.town;
        }
      }

      const response = await api.put(
        '/api/profile',
        cleanedData,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (response.data.success) {
        setProfile(response.data.profile);
        setHasUnsavedChanges(false);
        clearDraft();
        setLastAutoSaved(new Date());
      }
    } catch (error) {
      // Silent failure — don't interrupt the user, they can still manually save
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  }, [formData, hasUnsavedChanges, editing, saving, isWorker, profile, setProfile]);

  // Auto-save every 30 seconds after the last change
  useEffect(() => {
    if (!editing || !hasUnsavedChanges) return;
    const timer = setTimeout(() => autoSave(), 30000);
    return () => clearTimeout(timer);
  }, [formData, editing, hasUnsavedChanges, autoSave]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };
  
  // Format salary for display
  const formatSalary = (value) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(value);
  };
  
  // Render view mode (non-editable)
  if (!editing) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0 bg-background">
        <TopBar profile={profile} />
        
        <div className="px-4 py-6 max-w-6xl mx-auto space-y-6">
          {/* Back to a job application that was left to fill in the profile */}
          <ContinueApplicationCard navigate={navigate} t={t} />
          {/* Profile Header with Progress */}
          <Card className="relative overflow-hidden" data-testid="profile-header-card">
            <div 
              className="absolute inset-0 opacity-60 pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, hsl(176 36% 91%) 0%, hsl(180 30% 90%) 50%, hsl(48 47% 92%) 100%)'
              }}
            />
            <CardContent className="relative p-6">
              <div className="flex flex-col items-start gap-6">
                {/* Avatar & Info */}
                <div className="flex items-center gap-4 w-full">
                  <div className="relative flex-shrink-0">
                    <Avatar className="w-20 h-20 md:w-24 md:h-24" data-testid="profile-avatar">
                      {profile?.avatar_url && (
                        <AvatarImage src={profile.avatar_url} alt={profile.name} />
                      )}
                      <AvatarFallback className="text-2xl md:text-3xl font-heading">
                        {profile?.name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <label 
                      htmlFor="avatar-upload" 
                      className="absolute bottom-0 right-0 bg-primary rounded-full p-2 cursor-pointer hover:bg-primary/90 transition shadow-lg"
                    >
                      <Camera size={14} className="text-white" />
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                        disabled={uploadingAvatar}
                      />
                    </label>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl md:text-2xl font-heading font-bold break-words" data-testid="profile-name">
                      {profile?.name}
                    </h1>
                    {formData.tagline && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words" data-testid="profile-tagline">
                        {formData.tagline}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="secondary">{getRoleDisplayName(profile?.role, true)}</Badge>
                      {profile?.verified && (
                        <Badge className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold shadow-md border-teal-400 border" data-testid="verified-badge">
                          <Shield size={14} className="mr-1" />
                          True Boholano
                        </Badge>
                      )}
                      {profile?.rating >= 4.5 && profile?.reviews_count >= 3 && (
                        <span className="badge-top-rated" data-testid="top-rated-badge">
                          <Star size={14} className="fill-white" />
                          {t('profile.topRated') || 'Top Rated'}
                        </span>
                      )}
                    </div>
                    {formData.town && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground" data-testid="profile-location">
                        <MapPin size={14} className="flex-shrink-0" />
                        <span className="break-words">{formData.barangay && `${formData.barangay}, `}{formData.town}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Progress Circle (Workers Only) - Now in its own row on mobile */}
                {isWorker && completenessData && (
                  <div className="w-full flex flex-col items-center gap-3 py-4 border-t">
                    <CircularProgress 
                      value={completenessData.percentage} 
                      size={90}
                      stroke={8}
                    />
                    <p className="text-xs text-center text-muted-foreground max-w-[280px]">
                      {t('profileCompleteness.completeYourProfile')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Rating (if exists) */}
          {profile?.rating > 0 && (
            <button
              onClick={() => setShowReviewsModal(true)}
              className="flex items-center gap-2 text-lg hover:opacity-80 transition cursor-pointer"
              data-testid="rating-button"
            >
              <Star className="text-yellow-500 fill-yellow-500" size={20} />
              <span className="font-semibold">{profile.rating.toFixed(1)}</span>
              <span className="text-muted-foreground text-sm">
                ({profile.reviews_count || 0} reviews)
              </span>
            </button>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Left Column - Quick Info */}
            <div className="md:col-span-4 space-y-4">
              {/* Verification Card */}
              {isWorker && (
                <Card data-testid="verification-card">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield size={18} />
                      {t('verification.verification')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profile?.verified ? (
                      <div className="flex flex-col gap-2">
                        <Badge className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold shadow-md border-teal-400 border w-fit">
                          <Shield size={14} className="mr-1" />
                          True Boholano
                        </Badge>
                        <span className="text-xs text-muted-foreground">{t('landing.idVerified')}</span>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          {t('verification.verifyBuild')}
                        </p>
                        <Button
                          onClick={() => navigate('/verify-id')}
                          variant="outline"
                          className="w-full"
                          data-testid="verify-now-button"
                        >
                          <Shield size={16} className="mr-2" />
                          {t('verification.verifyNow')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Quick Actions */}
              <Card>
                <CardContent className="p-4 space-y-2">
                  <Button
                    onClick={() => setEditing(true)}
                    className="w-full"
                    data-testid="edit-profile-button"
                  >
                    <Edit size={16} className="mr-2" />
                    {t('profile.editProfile')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleSignOut}
                    className="w-full"
                    data-testid="signout-button"
                  >
                    <LogOut size={16} className="mr-2" />
                    {t('profile.signOut')}
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Right Column - Profile Details */}
            <div className="md:col-span-8 space-y-4">
              {isWorker ? (
                <>
                  {/* Section 1: Basics */}
                  <Card data-testid="basics-section">
                    <CardHeader>
                      <CardTitle>{t('profile.basicInformation')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground text-xs">{t('profile.email')}</Label>
                        <p className="text-sm">{profile?.email}</p>
                      </div>
                      {formData.phone && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.phone')}</Label>
                          <p className="text-sm">{formData.phone}</p>
                        </div>
                      )}
                      {isWorker && formData.date_of_birth && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.dateOfBirth', 'Date of Birth')}</Label>
                          <p className="text-sm">{new Date(formData.date_of_birth + 'T00:00:00').toLocaleDateString()}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Section 2: Experience & Skills */}
                  <Card data-testid="experience-section">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle>{t('profile.experienceSkills')}</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(true)}
                        className="text-xs"
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        {t('profile.addExperienceSkills')}
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {formData.years_of_experience && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.yearsOfExperience')}</Label>
                          <p className="text-sm">
                            {YEARS_OF_EXPERIENCE.find(y => y.value === formData.years_of_experience)?.label}
                          </p>
                        </div>
                      )}
                      {formData.skills && formData.skills.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.skills')}</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.skills.map((skill, i) => {
                              const skillLabel = WORKER_SKILLS.find(s => s.value === skill)?.label || skill;
                              return (
                                <Badge key={i} variant="secondary">{skillLabel}</Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {formData.work_history && formData.work_history.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.workHistory', 'Work History')}</Label>
                          <div className="mt-2 space-y-3">
                            {formData.work_history.map((entry, i) => (
                              <div key={i} className="p-3 rounded-lg border bg-muted/30">
                                <p className="text-sm font-semibold">{entry.position}</p>
                                <p className="text-sm text-muted-foreground">{entry.company}</p>
                                {entry.duration && (
                                  <p className="text-xs text-muted-foreground mt-1">{entry.duration}</p>
                                )}
                                {entry.description && (
                                  <p className="text-sm mt-2">{entry.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {formData.education_level && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.education')}</Label>
                          <p className="text-sm">
                            {getEducationLabel(formData.education_level)}
                            {formData.education_field && ` - ${formData.education_field}`}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Section 3: Job Preferences */}
                  <Card data-testid="preferences-section">
                    <CardHeader>
                      <CardTitle>{t('profile.jobPreferences')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {formData.salary_min && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.salaryExpectation')}</Label>
                          <p className="text-sm">
                            {formatSalary(formData.salary_min)} - {formatSalary(formData.salary_max)} 
                            {formData.salary_expectation_type === 'daily' ? ' per day' : ' per month'}
                          </p>
                        </div>
                      )}
                      {formData.availability && formData.availability.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.availability')}</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.availability.map((avail, i) => {
                              const availLabel = AVAILABILITY_OPTIONS.find(a => a.value === avail)?.label;
                              return <Badge key={i} variant="outline">{availLabel}</Badge>;
                            })}
                          </div>
                        </div>
                      )}
                      {formData.preferred_locations && formData.preferred_locations.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.preferredLocations')}</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.preferred_locations.map((loc, i) => (
                              <Badge key={i} variant="secondary">{loc}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Resume — sits next to Certifications & Licenses */}
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('profile.resume.heading', 'Resume')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResumeUploadCard
                        existingFileUrl={profile?.resume_file_url || null}
                        currentProfile={profile}
                        confirmBeforeApply={true}
                        onUploaded={(info) => {
                          // Reflect the new file URL in parent state immediately so the
                          // dashboard's "upload resume" banner disappears and this card
                          // shows "Resume on file" if the user revisits without reload.
                          setProfile((prev) => prev ? {
                            ...prev,
                            resume_file_url: info.file_url,
                            resume_uploaded_at: info.uploaded_at,
                          } : prev);
                        }}
                        onParsed={async (extracted) => {
                          // Update local form state with parsed fields (fields user can see/edit on this page)
                          const updates = {};
                          // `name` is locked by the backend once a user is verified — sending it
                          // makes the entire PUT fail with 400, dropping all parsed fields.
                          // Skip it for verified accounts; user can request a name change via support.
                          const skipName = !!profile?.verified;
                          const fields = ['name', 'phone', 'town', 'tagline', 'years_of_experience', 'education_level', 'education_field'];
                          for (const f of fields) {
                            if (skipName && f === 'name') continue;
                            const v = extracted[f];
                            if (v !== null && v !== undefined && v !== '') updates[f] = v;
                          }
                          if (Array.isArray(extracted.skills) && extracted.skills.length > 0) {
                            updates.skills = extracted.skills.slice(0, 12);
                          }
                          if (Array.isArray(extracted.work_history) && extracted.work_history.length > 0) {
                            updates.work_history = extracted.work_history.slice(0, 5);
                          }
                          if (Object.keys(updates).length === 0) return;
                          // Save to API — only commit to local state if the PUT actually persisted,
                          // otherwise the UI would silently lie about what's saved.
                          let saved = false;
                          try {
                            const { data: session } = await supabase.auth.getSession();
                            if (session?.session?.access_token) {
                              await api.put('/api/profile', updates, {
                                headers: { Authorization: `Bearer ${session.session.access_token}` },
                              });
                              saved = true;
                            }
                          } catch (err) {
                            console.error('Failed to save parsed fields:', err);
                            const detail = err?.response?.data?.detail;
                            toast.error(
                              detail
                                ? `Couldn't apply some fields: ${detail}`
                                : "Couldn't save the parsed fields. Try again."
                            );
                          }
                          if (saved) {
                            setFormData((prev) => ({ ...prev, ...updates }));
                            setProfile((prev) => prev ? { ...prev, ...updates, _parseConfidence: extracted._field_confidence || {} } : prev);
                          }
                        }}
                        onDeleted={() => {
                          setProfile((prev) => prev ? { ...prev, resume_file_url: null, resume_uploaded_at: null } : prev);
                        }}
                      />
                    </CardContent>
                  </Card>

                  {/* Certifications Section - Only for job_seeker and contractor */}
                  <CertificationsSection profile={profile} />

                  {/* Job alerts on/off (new-job push + in-app; default ON) */}
                  <JobAlertsSetting profile={profile} />

                  {/* SMS Notification Preferences */}
                  <SmsNotificationSettings profile={profile} />

                  {/* Password Change Section */}
                  <PasswordChangeSection profile={profile} />
                </>
              ) : (
                /* Non-worker profile view - Employer/Owner */
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('profile.contactInformation')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-muted-foreground text-xs">{t('profile.email')}</Label>
                        <p className="text-sm">{profile?.email}</p>
                      </div>
                      {formData.phone && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.phone')}</Label>
                          <p className="text-sm">{formData.phone}</p>
                        </div>
                      )}
                      {isWorker && formData.date_of_birth && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.dateOfBirth', 'Date of Birth')}</Label>
                          <p className="text-sm">{new Date(formData.date_of_birth + 'T00:00:00').toLocaleDateString()}</p>
                        </div>
                      )}
                      {formData.town && (
                        <div>
                          <Label className="text-muted-foreground text-xs">{t('profile.location')}</Label>
                          <p className="text-sm">{formData.town}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Business Information for Employer/Owner */}
                  {(formData.business_name || formData.is_private_household) && (
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('profile.businessInformation')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {formData.is_private_household ? (
                          <div className="rounded-lg bg-muted/50 p-3">
                            <p className="text-sm font-medium">{t('auth.privateHousehold', 'Private Household')}</p>
                            <p className="text-xs text-muted-foreground mt-1">{formData.business_name || `Private Household in ${formData.town || 'Bohol'}`}</p>
                          </div>
                        ) : (
                          <>
                            <div>
                              <Label className="text-muted-foreground text-xs">{t('profile.businessName')}</Label>
                              <p className="text-sm">{formData.business_name}</p>
                            </div>
                            {formData.business_type && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.businessType')}</Label>
                                <p className="text-sm">{formData.business_type}</p>
                              </div>
                            )}
                            {formData.industry && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.industry')}</Label>
                                <p className="text-sm">{formData.industry}</p>
                              </div>
                            )}
                            {formData.business_address && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.businessAddress')}</Label>
                                <p className="text-sm">{formData.business_address}</p>
                              </div>
                            )}
                            {formData.years_in_business && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.yearsInBusiness')}</Label>
                                <p className="text-sm">{formData.years_in_business} years</p>
                              </div>
                            )}
                            {formData.number_of_employees && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.numberOfEmployees')}</Label>
                                <p className="text-sm">{formData.number_of_employees}</p>
                              </div>
                            )}
                            {formData.business_description && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.businessDescription')}</Label>
                                <p className="text-sm">{formData.business_description}</p>
                              </div>
                            )}
                            {formData.website && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('profile.website')}</Label>
                                <p className="text-sm">{formData.website}</p>
                              </div>
                            )}
                            {formData.facebook_page && (
                              <div>
                                <Label className="text-muted-foreground text-xs">{t('auth.facebookPage', 'Facebook Page')}</Label>
                                <a href={formData.facebook_page} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">{formData.facebook_page}</a>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* SMS Notification Preferences */}
                  <SmsNotificationSettings profile={profile} />

                  {/* Password Change Section */}
                  <PasswordChangeSection profile={profile} />

                  {/* Verification Card for Employer/Owner */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield size={20} className={profile?.verified ? "text-teal-600" : "text-muted-foreground"} />
                        {t('verification.idVerification')}
                      </CardTitle>
                      <CardDescription>
                        {t('verification.verifyBusinessToBuildTrust')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {profile?.verified ? (
                        <div className="flex items-center gap-3 p-4 bg-teal-50 dark:bg-teal-950 rounded-xl border border-teal-200 dark:border-teal-800">
                          <CheckCircle className="text-teal-600" size={24} />
                          <div>
                            <p className="font-semibold text-teal-900 dark:text-teal-100">{t('verification.idVerified')}</p>
                            <p className="text-sm text-teal-700 dark:text-teal-300">{t('verification.yourIdHasBeenVerified')}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-xl border border-yellow-200 dark:border-yellow-800">
                            <p className="text-sm text-yellow-800 dark:text-yellow-200">
                              <strong>{t('verification.notVerified')}</strong> - {t('verification.completeIdVerificationToIncreaseTrust')}
                            </p>
                          </div>
                          <Button
                            onClick={() => navigate('/verify-id')}
                            className="w-full rounded-xl"
                            data-testid="verify-business-btn"
                          >
                            <Shield size={18} className="mr-2" />
                            {t('verification.verifyIdentity')}
                          </Button>
                          <p className="text-xs text-muted-foreground text-center">
                            {t('verification.youllNeedFrontBackSelfie')}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
        
        <ReviewsListModal
          open={showReviewsModal}
          onOpenChange={setShowReviewsModal}
          userId={profile?.id}
          userName={profile?.name}
        />
        
        {/* Verification handled by /verify-id page */}
        
        <BottomNav />
      </div>
    );
  }
  
  // EDIT MODE - Comprehensive form
  return (
    <div className="min-h-screen-safe pb-32 md:pb-24 bg-background">
      <TopBar profile={profile} />
      
      <div className="px-4 py-6 max-w-6xl mx-auto space-y-6">
        {/* Edit Mode Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">{t('profile.editProfile')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('profile.updateProfileInfo')}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left Column - Progress */}
          {isWorker && completenessData && (
            <div className="md:col-span-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('profileCompleteness.profileCompleteness')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-4">
                  <CircularProgress 
                    value={completenessData.percentage} 
                    size={120}
                    stroke={10}
                  />
                  <div className="w-full space-y-2">
                    {completenessData.checklist.map((item) => {
                      // Map backend labels to translation keys
                      const labelMap = {
                        'Profile Photo': t('profileCompleteness.profilePhoto'),
                        'Full Name': t('profileCompleteness.fullName'),
                        'Phone Number': t('profileCompleteness.phoneNumber'),
                        'Date of Birth': t('profileCompleteness.dateOfBirth', 'Date of Birth'),
                        'Town/City': t('profileCompleteness.townCity'),
                        'Barangay': t('profileCompleteness.barangay'),
                        'Short Tagline': t('profileCompleteness.shortTagline'),
                        'Years of Experience': t('profileCompleteness.yearsOfExperience'),
                        'Main Skills': t('profileCompleteness.mainSkills'),
                        'Work History': t('profileCompleteness.workHistory'),
                        'Education Level': t('profileCompleteness.educationLevel'),
                        'Salary Expectation': t('profileCompleteness.salaryExpectation'),
                        'Availability': t('profileCompleteness.availability'),
                        'Preferred Work Locations': t('profileCompleteness.preferredWorkLocations'),
                        'Preferred Work Type': t('profileCompleteness.preferredWorkType'),
                        'ID Verification': t('profileCompleteness.idVerification')
                      };
                      
                      return (
                        <div key={item.key} className="flex items-center gap-2 text-sm">
                          {item.done ? (
                            <Check size={16} className="text-success" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-muted" />
                          )}
                          <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>
                            {labelMap[item.label] || item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Right Column - Edit Form */}
          <div className={`${isWorker ? 'md:col-span-8' : 'md:col-span-12'} space-y-6`}>
            {isWorker ? (
              <>
                {/* Section 1: Basics */}
                <Card data-testid="edit-basics-section">
                  <CardHeader>
                    <CardTitle>{t('profile.section1Basics')}</CardTitle>
                    <CardDescription>{t('profile.basicContactLocation')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">{t('profile.fullName')} *</Label>
                        <div className="relative">
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder={t('profile.placeholderFullName')}
                            data-testid="input-name"
                            disabled={profile?.verified}
                            className={profile?.verified ? 'pr-8 bg-muted' : ''}
                          />
                          {profile?.verified && <Lock className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />}
                        </div>
                        {profile?.verified && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> {t('profile.fieldLockedAfterVerification', 'Locked after ID verification. Contact support to change.')}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="phone">{t('profile.phoneNumber')} *</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder={t('profile.placeholderPhone')}
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    {isWorker && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="date_of_birth">{t('profile.dateOfBirth', 'Date of Birth')} *</Label>
                          <Input
                            id="date_of_birth"
                            type="date"
                            value={formData.date_of_birth}
                            onChange={(e) => handleChange('date_of_birth', e.target.value)}
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            min="1925-01-01"
                            data-testid="input-date-of-birth"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="town">{t('profile.townCity')} *</Label>
                        <Select 
                          value={formData.town} 
                          onValueChange={(value) => handleChange('town', value)}
                        >
                          <SelectTrigger data-testid="select-town">
                            <SelectValue placeholder={t('profile.selectYourTown')} />
                          </SelectTrigger>
                          <SelectContent>
                            {BOHOL_TOWNS.map((town) => (
                              <SelectItem key={town} value={town}>{town}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="barangay">{t('profile.barangay')} *</Label>
                        <Select
                          value={formData.barangay}
                          onValueChange={(value) => handleChange('barangay', value)}
                        >
                          <SelectTrigger data-testid="select-barangay">
                            <SelectValue placeholder={t('profile.selectBarangay')} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {availableBarangays.map((barangay) => (
                              <SelectItem key={barangay} value={barangay}>{barangay}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="tagline">{t('profile.shortTagline')} * <span className="text-muted-foreground">({t('profile.max80Chars')})</span></Label>
                      <Input
                        id="tagline"
                        value={formData.tagline}
                        onChange={(e) => handleChange('tagline', e.target.value.slice(0, 80))}
                        placeholder={t('profile.placeholderTagline')}
                        maxLength={80}
                        data-testid="input-tagline"
                      />
                      <div className="text-xs text-muted-foreground text-right mt-1">
                        {formData.tagline.length}/80
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Section 2: Experience & Skills */}
                <Card data-testid="edit-experience-section">
                  <CardHeader>
                    <CardTitle>{t('profile.experienceSkills')}</CardTitle>
                    <CardDescription>{t('profile.showEmployersWhatYouCanDo')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label htmlFor="years-exp">{t('profile.yearsOfExperience')} *</Label>
                      <Select
                        value={formData.years_of_experience}
                        onValueChange={(value) => handleChange('years_of_experience', value)}
                      >
                        <SelectTrigger data-testid="select-years-exp">
                          <SelectValue placeholder={t('profile.selectYourExperienceLevel')} />
                        </SelectTrigger>
                        <SelectContent>
                          {YEARS_OF_EXPERIENCE.map((exp) => (
                            <SelectItem key={exp.value} value={exp.value}>
                              {exp.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>{t('profile.mainSkills')} * <span className="text-muted-foreground">({t('profile.selectUpTo12')})</span></Label>
                      <div className="mt-2">
                        <SkillsSelector
                          options={WORKER_SKILLS}
                          value={formData.skills}
                          onChange={(value) => handleChange('skills', value)}
                          max={12}
                          testId="skills-chips"
                          placeholder={t('profile.selectYourSkills')}
                        />
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <Label>{t('profile.workHistory')} <span className="text-muted-foreground">({t('profile.upTo5Entries')})</span></Label>
                      <div className="mt-2">
                        <WorkHistoryItem
                          value={formData.work_history}
                          onChange={(value) => handleChange('work_history', value)}
                          max={5}
                        />
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="education">{t('profile.educationLevel')} *</Label>
                        <Select
                          value={formData.education_level}
                          onValueChange={(value) => handleChange('education_level', value)}
                        >
                          <SelectTrigger data-testid="select-education">
                            <SelectValue placeholder={t('profile.selectYourEducationLevel')} />
                          </SelectTrigger>
                          <SelectContent>
                            {EDUCATION_LEVELS.map((edu) => (
                              <SelectItem key={edu.value} value={edu.value}>
                                {edu.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {['college_graduate', 'bachelors', 'masters', 'doctorate'].includes(formData.education_level) && (
                        <div>
                          <Label htmlFor="education-field">{t('profile.fieldOfStudy')}</Label>
                          <Input
                            id="education-field"
                            value={formData.education_field}
                            onChange={(e) => handleChange('education_field', e.target.value)}
                            placeholder={t('profile.placeholderFieldOfStudy')}
                            data-testid="input-education-field"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Section 3: Job Preferences */}
                <Card data-testid="edit-preferences-section">
                  <CardHeader>
                    <CardTitle>{t('profile.jobPreferences')}</CardTitle>
                    <CardDescription>{t('profile.helpEmployersFindYou')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <Label>{t('profile.salaryExpectation')} *</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={formData.salary_expectation_type === 'daily' ? 'default' : 'outline'}
                            onClick={() => {
                              handleChange('salary_expectation_type', 'daily');
                              handleChange('salary_min', 400);
                              handleChange('salary_max', 600);
                            }}
                            data-testid="salary-type-daily"
                          >
                            {t('profile.daily')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={formData.salary_expectation_type === 'monthly' ? 'default' : 'outline'}
                            onClick={() => {
                              handleChange('salary_expectation_type', 'monthly');
                              handleChange('salary_min', 15000);
                              handleChange('salary_max', 25000);
                            }}
                            data-testid="salary-type-monthly"
                          >
                            {t('profile.monthly')}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>{t('profile.minimum')}: {formatSalary(formData.salary_min)}</span>
                            <span>{t('profile.maximum')}: {formatSalary(formData.salary_max)}</span>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <Label className="text-xs text-muted-foreground">{t('profile.minimumSalary')}</Label>
                              <Input
                                type="number"
                                value={formData.salary_min === 0 ? '' : formData.salary_min}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                                  handleChange('salary_min', val);
                                }}
                                className="mt-2"
                                placeholder={t('profile.enterAnyAmount')}
                                min="0"
                              />
                              <p className="text-xs text-muted-foreground mt-1">{t('profile.enterAnyAmount')}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">{t('profile.maximumSalary')}</Label>
                              <Input
                                type="number"
                                value={formData.salary_max === 0 ? '' : formData.salary_max}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? 0 : parseInt(e.target.value);
                                  handleChange('salary_max', val);
                                }}
                                className="mt-2"
                                placeholder={t('profile.enterAnyAmount')}
                                min="0"
                              />
                              <p className="text-xs text-muted-foreground mt-1">{t('profile.enterAnyAmount')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <Label>{t('profile.availability')} *</Label>
                      <div className="mt-2">
                        <MultiSelectChips
                          options={AVAILABILITY_OPTIONS}
                          value={formData.availability}
                          onChange={(value) => handleChange('availability', value)}
                          max={5}
                          testId="availability-chips"
                          placeholder={t('profile.whenCanYouStart')}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>{t('profile.preferredWorkLocations')} * <span className="text-muted-foreground">({t('profile.max5Towns')})</span></Label>
                      <div className="mt-2">
                        <MultiSelectChips
                          options={BOHOL_TOWNS.map(town => ({ value: town, label: town }))}
                          value={formData.preferred_locations}
                          onChange={(value) => handleChange('preferred_locations', value)}
                          max={5}
                          testId="locations-chips"
                          placeholder={t('profile.selectPreferredTowns')}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>{t('profile.preferredWorkType')} *</Label>
                      <div className="mt-2">
                        <MultiSelectChips
                          options={WORK_TYPE_OPTIONS}
                          value={formData.preferred_work_types}
                          onChange={(value) => handleChange('preferred_work_types', value)}
                          max={4}
                          testId="work-types-chips"
                          placeholder={t('profile.whatTypeOfWorkDoYouPrefer')}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

              </>
            ) : (
              /* Non-worker edit form - Employer/Owner */
              <>
                <Card data-testid="edit-contact-section">
                  <CardHeader>
                    <CardTitle>{t('profile.contactInformation')}</CardTitle>
                    <CardDescription>{t('profile.yourContactDetails')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="phone">{t('profile.phoneNumber')} *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="town">{t('profile.townCity')} *</Label>
                      <Select
                        value={formData.town}
                        onValueChange={(value) => handleChange('town', value)}
                        disabled={profile?.verified}
                      >
                        <SelectTrigger data-testid="select-town" className={profile?.verified ? 'bg-muted' : ''}>
                          <SelectValue placeholder="Select your town" />
                        </SelectTrigger>
                        <SelectContent>
                          {BOHOL_TOWNS.map((town) => (
                            <SelectItem key={town} value={town}>{town}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {profile?.verified && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Lock className="h-3 w-3" /> {t('profile.fieldLockedAfterVerification', 'Locked after ID verification. Contact support to change.')}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="tagline">{t('profile.shortTagline')} <span className="text-muted-foreground">({t('profile.max80Chars')})</span></Label>
                      <Input
                        id="tagline"
                        value={formData.tagline}
                        onChange={(e) => handleChange('tagline', e.target.value.slice(0, 80))}
                        placeholder="e.g., Trusted employer since 2010"
                        maxLength={80}
                        data-testid="input-tagline"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="edit-business-section">
                  <CardHeader>
                    <CardTitle>{t('profile.businessInformation')}</CardTitle>
                    <CardDescription>{t('profile.detailsAboutYourBusiness')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {formData.is_private_household ? (
                      /* Private household — read-only info */
                      <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                        <p className="text-sm font-medium">{t('auth.privateHousehold', 'Private Household')}</p>
                        <p className="text-xs text-muted-foreground">
                          {formData.business_name || `Private Household in ${formData.town || 'Bohol'}`}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t('auth.privateHouseholdNote', 'Your listing will appear as "Private Household in [your municipality]". Perfect for hiring nannies, kasambahay, drivers, caretakers, etc.')}
                        </p>
                      </div>
                    ) : (
                      /* Business employer — full edit form */
                      <>
                        <div>
                          <Label htmlFor="business-name">{t('profile.businessName')} *</Label>
                          <div className="relative">
                            <Input
                              id="business-name"
                              value={formData.business_name}
                              onChange={(e) => handleChange('business_name', e.target.value)}
                              placeholder="e.g., Bohol Construction Inc."
                              data-testid="input-business-name"
                              disabled={profile?.verified}
                              className={profile?.verified ? 'pr-8 bg-muted' : ''}
                            />
                            {profile?.verified && <Lock className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" />}
                          </div>
                          {profile?.verified ? (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <Lock className="h-3 w-3" /> {t('profile.fieldLockedAfterVerification', 'Locked after ID verification. Contact support to change.')}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">This is the name displayed publicly on your job posts and profile.</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="business-type">{t('profile.businessType')} *</Label>
                            <Select
                              value={formData.business_type}
                              onValueChange={(value) => handleChange('business_type', value)}
                            >
                              <SelectTrigger data-testid="select-business-type">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Sole Proprietor">Sole Proprietor</SelectItem>
                                <SelectItem value="Partnership">Partnership</SelectItem>
                                <SelectItem value="Corporation">Corporation</SelectItem>
                                <SelectItem value="Cooperative">Cooperative</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="industry">{t('profile.industry')} *</Label>
                            <Select
                              value={formData.industry}
                              onValueChange={(value) => handleChange('industry', value)}
                            >
                              <SelectTrigger data-testid="select-industry">
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Construction">Construction</SelectItem>
                                <SelectItem value="Hospitality">Hospitality</SelectItem>
                                <SelectItem value="Retail">Retail</SelectItem>
                                <SelectItem value="Services">Services</SelectItem>
                                <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                                <SelectItem value="Technology">Technology</SelectItem>
                                <SelectItem value="Agriculture">Agriculture</SelectItem>
                                <SelectItem value="Healthcare">Healthcare</SelectItem>
                                <SelectItem value="Education">Education</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="business-address">{t('profile.businessAddress')} *</Label>
                          <Input
                            id="business-address"
                            value={formData.business_address}
                            onChange={(e) => handleChange('business_address', e.target.value)}
                            placeholder="Complete business address"
                            data-testid="input-business-address"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="years-in-business">{t('profile.yearsInBusiness')}</Label>
                            <Input
                              id="years-in-business"
                              type="number"
                              value={formData.years_in_business}
                              onChange={(e) => handleChange('years_in_business', parseInt(e.target.value) || 0)}
                              placeholder="e.g., 5"
                              min="0"
                              data-testid="input-years-in-business"
                            />
                          </div>

                          <div>
                            <Label htmlFor="number-of-employees">{t('profile.numberOfEmployees')}</Label>
                            <Select
                              value={formData.number_of_employees}
                              onValueChange={(value) => handleChange('number_of_employees', value)}
                            >
                              <SelectTrigger data-testid="select-employees">
                                <SelectValue placeholder="Select range" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1-5">1-5</SelectItem>
                                <SelectItem value="6-10">6-10</SelectItem>
                                <SelectItem value="11-20">11-20</SelectItem>
                                <SelectItem value="21-50">21-50</SelectItem>
                                <SelectItem value="51-100">51-100</SelectItem>
                                <SelectItem value="100+">100+</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="business-description">{t('profile.businessDescription')} *</Label>
                          <Textarea
                            id="business-description"
                            value={formData.business_description}
                            onChange={(e) => handleChange('business_description', e.target.value)}
                            placeholder="Describe your business, services, or products..."
                            rows={4}
                            data-testid="input-business-description"
                          />
                        </div>

                        <div>
                          <Label htmlFor="website">{t('profile.website')} ({t('common.optional')})</Label>
                          <Input
                            id="website"
                            type="url"
                            value={formData.website}
                            onChange={(e) => handleChange('website', e.target.value)}
                            placeholder="https://yourbusiness.com"
                            data-testid="input-website"
                          />
                        </div>

                        <div>
                          <Label htmlFor="facebook-page">{t('auth.facebookPage', 'Facebook Page')} ({t('common.optional')})</Label>
                          <Input
                            id="facebook-page"
                            type="url"
                            value={formData.facebook_page || ''}
                            onChange={(e) => handleChange('facebook_page', e.target.value)}
                            placeholder="https://facebook.com/yourbusiness"
                            data-testid="input-facebook-page"
                          />
                          <p className="text-xs text-muted-foreground mt-1">{t('auth.facebookPageHint', 'Link to your business Facebook page — shown on your employer profile.')}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Save Bar */}
      <SaveBar
        visible={true}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saving={saving}
        autoSaving={autoSaving}
        lastAutoSaved={lastAutoSaved}
      />
      
      <BottomNav />
    </div>
  );
};

export default ProfileNew;
